/**
 * Manual de usuario (español) — Phase 4.
 *
 * Cada sección es un objeto con:
 *   id        – ancla URL (#id)
 *   title     – título visible
 *   icon      – nombre del icono lucide-react
 *   intro     – párrafo de introducción (opcional)
 *   body      – markdown crudo, renderizado por react-markdown
 *   tips      – array de callouts con `kind` ∈ "tip" | "warning" y `text`
 *   anchor    – key de l10n para el botón "Más info" desde tooltips
 *
 * IMPORTANTE: el manual describe la app REAL (no inventes pasos). Si añades
 * features, actualiza esta sección y la versión `manual.en.js` en paralelo.
 */

const manual = [
  {
    id: "intro",
    title: "Introducción",
    icon: "BookOpen",
    intro:
      "Distribución Canales Dashboard es una herramienta web para medir la productividad de los empleados de bodega a partir de los reportes exportados por el ERP.",
    body: `### ¿Qué hace esta app?

La aplicación parsea archivos Excel del ERP y los transforma en dashboards visuales para que puedas:

- Ver el **volumen de transacciones por empleado** (picks, recibos, traslados, etc.).
- Identificar al **Ganador del mes** por volumen o por eficiencia (tx/hr).
- Comparar empleados, departamentos y periodos.
- Detectar cuellos de botella con vistas como Heatmap, Tendencia, Distribución y 3D.
- Compartir el dashboard en una **TV** o por **link público con QR**, sin necesidad de cuenta.

### Tipos de reporte soportados

| Archivo | Contenido |
| --- | --- |
| GENDETAILED_PROD | Transacciones detalladas (RF PICK, RF STAGE, RF TRNS, RF RCPT, RF.RCVR). |
| IC385DETAIL | Inventario / ajustes (variantes del mismo ERP). |
| IC385P2DETAIL | Variante P2 del IC385. |
| TE350 | **Horas trabajadas** por empleado (necesarias para Eficiencia tx/hr). |

### Stack

Web-first (React + FastAPI + MongoDB) con zona horaria fija \`America/Chicago\`. Los datos se deduplican por hash al subirlos — puedes re-subir el mismo archivo sin miedo.`,
  },
  {
    id: "acceso",
    title: "Acceso e ingreso",
    icon: "LogIn",
    body: `### Iniciar sesión

1. Abre la app y serás llevado automáticamente a la pantalla de login.
2. Ingresa tu correo y contraseña.
3. La app te redirige a la **primera vista que tu rol pueda ver** (por defecto el Dashboard). Si tu cuenta es viewer con permisos limitados, te llevará directo a tu reporte permitido — no verás 403.

### Credenciales por defecto del admin

- **Email:** \`admin@bodega.com\`
- **Contraseña:** \`Admin123!\`

> Al primer login el admin debe **cambiar esta contraseña** desde *Administración → Usuarios → tu cuenta → Cambiar contraseña*. Las credenciales vienen pre-seedeadas para acelerar el setup, pero quedan visibles a cualquiera que lea esta documentación.

### Cerrar sesión

Click en tu avatar (esquina superior derecha) → **Cerrar sesión**. El token JWT se descarta del navegador y vuelves al login.

### ¿Olvidé mi contraseña?

Esta versión NO tiene autoservicio de recuperación. Pide al administrador que entre en *Usuarios → tu cuenta → Cambiar contraseña* y te asigne una nueva.

### Idioma y tema

En la topbar tienes dos toggles: **Languages (ES/EN)** y **Sol/Luna** (light/dark). Tu elección se guarda en \`localStorage\` y persiste entre sesiones.`,
    tips: [
      {
        kind: "warning",
        text: "Cambia la contraseña por defecto del admin inmediatamente. Las credenciales \"Admin123!\" están públicas en este manual.",
      },
    ],
  },
  {
    id: "primer-upload",
    title: "Tu primer upload",
    icon: "Upload",
    body: `### Pasos

1. En el sidebar haz click en **Cargar reporte** (icono nube ↑). Necesitas el permiso \`upload\`.
2. Arrastra el archivo \`.xlsx\` a la zona punteada **o** haz click para seleccionarlo. Tamaño recomendado: hasta ~10 MB.
3. El selector **Tipo de reporte** intenta autodetectar el archivo por su nombre. Si no lo detecta (porque alguien renombró el archivo), elige el tipo manualmente.
4. Haz click en **Ingestar reporte**. La barra muestra "Procesando…". En unos segundos verás el resumen:
   - **Filas totales**: filas válidas en el Excel.
   - **Insertadas**: nuevas filas guardadas en Mongo.
   - **Duplicados**: filas que ya existían (mismo hash) y se omitieron.
   - **ID de carga**: identificador único de esta ingesta (queda en historial).
5. Las cargas TE350 muestran **Horas insertadas / Horas duplicadas** en lugar de transacciones.

### ¿Cómo verifica el sistema los duplicados?

Cada fila se hashea con SHA-256 sobre la combinación \`(employee_id, transaction_type, timestamp, item, qty, order_id, source_report)\`. Si re-subes el mismo archivo verás **0 insertadas / N duplicados** — eso es normal y prueba que la app es idempotente.

### Lista negra del sistema

Las filas con \`employee_id\` que contiene \`SYSTEM\`, \`CLOCK\`, \`AUCRON\`, \`TEST\`, \`DISPT\`, \`DEFAULT\`, \`STORE OWNER\` se descartan automáticamente — son entradas técnicas del ERP, no empleados reales.

### Historial de cargas

*Administración → Historial de cargas*. Muestra cada ingesta con archivo, tipo, usuario, fecha, totales y conteos. Útil para auditoría.`,
    tips: [
      {
        kind: "tip",
        text: "Si el ERP exporta varios archivos al día (GENDETAILED en la mañana + TE350 al final del día), súbelos en orden. El dedupe los une sin duplicar nada.",
      },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: "LayoutDashboard",
    body: `### Vista global

El Dashboard (\`/dashboard\`) es la pantalla principal. Muestra:

- **4 KPIs**: Transacciones totales, Empleados activos, Top empleado, Tipo dominante.
- **2 KPIs adicionales** (si subiste TE350): Promedio tx/hr y Top empleado eficiente.
- **Mensaje AI** de seguridad / productividad (ver sección 12).
- **Ganador destacado** del periodo (con foto-inicial, total de transacciones y enlace a "Ver completo").
- **Top 10 ranking** (gráfico horizontal por departamento).
- **Distribución por tipo** (donut con porcentajes).
- **Tendencia mensual** (líneas multi-serie por tipo de transacción).

### Filtros globales

Arriba del Dashboard hay un panel **Filtros**. Cada combinación se persiste durante la navegación entre reportes — el mismo filtro se aplica al resto de vistas.

Campos:

- **Periodo (preset)**: Hoy / Ayer / Esta semana / Este mes / Este año / Personalizado.
- **Desde / Hasta** (sólo en Personalizado).
- **Fuente**: limitar a un solo reporte (GENDETAILED, IC385, IC385P2).
- **Tipos de transacción**: chips multi-select.
- **Departamentos**: chips multi-select (cada uno con su color).
- **Empleados**: multi-select (filtra después de los anteriores).

Click en **Restablecer** para volver al preset por defecto (\`year\`).

### Drill-through

Click en el avatar del Ganador destacado → te lleva a **/reports/ganador** con el mismo filtro.`,
  },
  {
    id: "ganador",
    title: "Ganador del periodo",
    icon: "Trophy",
    body: `### Modo Volumen vs Eficiencia

En \`/reports/ganador\` tienes dos modos:

- **Volumen** (default): el empleado con más transacciones gana.
- **Eficiencia tx/hr**: el empleado con mejor ratio tx/hr gana. **Requiere TE350 subido**; si no, este modo aparece vacío.

### Periodo y "Anchor"

- **Periodo**: Día / Semana / Mes / Año.
- **Apuntar a (anchor)**: vacío = auto-detecta el periodo más activo. Puedes escribir manualmente p.ej. \`2026-05\` o \`2026-W22\`.

### Podio 3D Oro / Plata / Bronce

Bajo el ganador destacado verás un podio con 3 pilares 3D (CSS puro, no librería) que muestran los **Top 3** del periodo con badge de medalla y total. Pasa el mouse para ver el efecto de hover.

### Top 3 por tipo de transacción

5 mini-podios — uno por cada \`RF PICK / RF STAGE / RF TRNS / RF RCPT / RF.RCVR\` — con el Top 3 de empleados para ese tipo concreto en el periodo seleccionado.

### Confetti

Cuando hay ganador, una animación CSS-only de confeti aparece sobre la card del ganador. Es decorativa — no afecta a performance.`,
    tips: [
      {
        kind: "tip",
        text: "Si necesitas saber \"¿quién mueve más cajas por hora?\" usa modo Eficiencia. Si el área de trabajo es muy distinto por empleado (un picker no compara con un receptor), filtra por tipo de transacción para que sean comparables.",
      },
    ],
  },
  {
    id: "ranking",
    title: "Ranking",
    icon: "Award",
    body: `### Top empleados

\`/reports/ranking\` muestra un gráfico de barras **horizontal apilado por tipo de transacción**. Cada barra es un empleado, y los segmentos de colores indican cuánto contribuye cada tipo a su total.

### Controles

- **Top N**: 5, 10, 20 o 50 empleados (default 20).
- Las barras están coloreadas por departamento.

### Drill-through

**Click sobre el nombre de un empleado** (o sobre cualquier barra) → te lleva a **/reports/comparativo?compare=Z21** con ese empleado pre-seleccionado.`,
  },
  {
    id: "comparativo",
    title: "Comparativo",
    icon: "Scale",
    body: `### Comparar empleados lado a lado

\`/reports/comparativo\` permite seleccionar **hasta 10 empleados** y verlos en un gráfico de barras agrupado por tipo de transacción.

Pasos:

1. Click en los empleados del selector (cada uno se vuelve un chip).
2. Mira el gráfico actualizarse en tiempo real.
3. Si alcanzas 10, los demás se desactivan.

### Llegando desde Ranking

Si entras vía drill-through (\`?compare=Z21\`), ese empleado viene pre-seleccionado. Añade los que quieras comparar.`,
  },
  {
    id: "tendencia",
    title: "Tendencia",
    icon: "TrendingUp",
    body: `### Evolución temporal

\`/reports/tendencia\` grafica el conteo de transacciones a lo largo del tiempo.

### Controles

- **Granularidad**: Diaria / Semanal / Mensual.
- **Series por**: Tipo de transacción (default — una línea por RF PICK, RF STAGE, etc.), Empleado o Departamento. Cuando son muchas series, considera usar el filtro de departamentos primero.`,
  },
  {
    id: "distribucion",
    title: "Distribución por tipo",
    icon: "PieChart",
    body: `\`/reports/distribucion\` muestra un donut/pie con la proporción de cada tipo de transacción.

Cada slice tiene su color fijo (paleta Tokyo-Night: rosa para RF PICK, mostaza RF STAGE, verde RF TRNS, cyan RF RCPT, púrpura RF.RCVR) y un porcentaje. Pasa el mouse para ver el conteo absoluto.`,
  },
  {
    id: "heatmap",
    title: "Heatmap empleado × día",
    icon: "Flame",
    body: `\`/reports/heatmap\` muestra una tabla con empleados en filas y días de la semana en columnas. La celda se colorea según la intensidad (escala teal monocromática). El valor máximo del filtro aparece en el header como referencia.

Útil para detectar:

- Quién trabaja sólo lunes-jueves.
- Quién carga toda la semana.
- Días "muertos" en el almacén.`,
  },
  {
    id: "tabla",
    title: "Tabla detallada",
    icon: "TableProperties",
    body: `### Datos crudos

\`/reports/tabla\` muestra las transacciones individuales con paginación. Es la vista cuando necesitas ver la fila concreta — por ejemplo "¿qué ítem movió Diana a las 14:23 del 18 de mayo?".

### Filtros propios

- Buscador full-text por empleado, item u orden.
- Por página: 25 / 50 / 100 / 200.

### Exportar

- **Excel (.xlsx)** o **CSV** con el resultado filtrado.
- Tope de seguridad: **500 filas** por export para no explotar la memoria. Si necesitas más, refina filtros.`,
    tips: [
      {
        kind: "warning",
        text: "El export está limitado a 500 filas por petición. Es intencional — descargar 100k filas en un solo Excel rompe el navegador. Aplica filtros por mes o por departamento.",
      },
    ],
  },
  {
    id: "eficiencia",
    title: "Eficiencia tx/hr",
    icon: "Zap",
    body: `### tx por hora trabajada

\`/reports/efficiency\` combina las transacciones con las horas registradas en TE350 para calcular **tx/hr** por empleado.

### Visualizaciones

- **3 KPIs**: Promedio tx/hr, Top empleado eficiente, Horas totales.
- **Scatter** (Horas vs Transacciones, tamaño del bubble = tx/hr): permite ver cuadrantes — empleados con muchas horas pero poco volumen aparecen abajo-derecha, los muy eficientes arriba-izquierda.
- **Tabla** con ranking ordenado por tx/hr.

### ¿No veo nada?

Si la vista dice "Sin horas registradas", **falta subir el TE350**. Ve a *Cargar reporte → TE350*. La eficiencia se recalcula automáticamente al siguiente refresh.`,
  },
  {
    id: "vista3d",
    title: "Vista 3D",
    icon: "Boxes",
    body: `\`/reports/3d\` ofrece tres visualizaciones tridimensionales con Plotly:

### Tipos de visualización

- **Barras 3D**: top N empleados × tipos de transacción → altura = volumen.
- **Scatter 3D**: empleados × día de la semana × volumen, con bubble size = log(count).
- **Superficie**: top N empleados × días de la semana → relieve continuo. Útil para detectar patrones.

### Controles

- **Top N** (10, 20, 30, 50).
- **Reset cámara** (vuelve a la posición inicial).
- **Descargar PNG** (1600 × 900).
- **Pantalla completa**.

### Performance

Plotly se carga **bajo demanda** (~3 MB) la primera vez que abres la vista. Después es instantáneo.`,
  },
  {
    id: "empleados-canonicos",
    title: "Empleados canónicos",
    icon: "Combine",
    body: `### El problema

El ERP a veces registra al mismo empleado con varios códigos. Diana Arellano aparece como \`Z21\` en GENDETAILED y como \`089\` en IC385. Si no las unificas, en el ranking aparecen como dos personas distintas.

### La solución: empleados canónicos

\`/admin/employees\` reúne todos los códigos de un mismo empleado bajo una entidad **canónica** identificada por su nombre normalizado.

### Operaciones

- **Fusionar (Merge)**: combina los aliases del empleado B en el canónico A y elimina B.
- **Dividir (Split)**: cuando dos empleados quedaron unidos por error (ej. mismo nombre, distinta persona), mueve aliases seleccionados a un nuevo canónico.
- **Reconstruir índice**: re-procesa todas las transacciones para detectar nuevos aliases. Lánzalo después de subir un Excel nuevo si los nombres cambiaron.

### Bulk delete (soft)

Selecciona varios empleados → click **Borrar seleccionados**. Quedan **marcados como inactivos**, no se borra ninguna transacción. Aparecen con badge "Inactivo" si marcas el toggle **Mostrar inactivos**. Para reactivar uno, click sobre él → **Reactivar empleado**.

> La data histórica nunca se pierde: borrar un canónico sólo lo oculta de las vistas. Los reportes respetan el flag de inactividad.`,
  },
  {
    id: "departamentos",
    title: "Departamentos",
    icon: "Building2",
    body: `\`/admin/departments\` permite agrupar empleados Z## en departamentos para reportes por grupo.

### Pasos

1. **Agregar departamento** → escribe nombre, escoge color (hex), confirma.
2. Click en el card → **Agregar código (Z##)** → escribe el código del empleado y enter.
3. El color del departamento se usa automáticamente en gráficos (barras del ranking, podio, etc.).
4. Editar / Eliminar desde el menú de tres puntos.

### Permisos

Necesitas \`dept:manage\`. Los viewers ven los colores pero no pueden modificar.`,
  },
  {
    id: "usuarios-permisos",
    title: "Usuarios y permisos",
    icon: "Users",
    body: `\`/admin/users\` gestiona accesos. Sólo el admin puede ver esta pantalla.

### Roles

- **admin**: acceso total, todos los permisos.
- **viewer**: acceso restringido a los permisos que le asignes.

### Permisos disponibles

| Permiso | Qué desbloquea |
| --- | --- |
| \`upload\` | Subir reportes Excel |
| \`users:manage\` | Gestionar usuarios y permisos |
| \`dept:manage\` | Gestionar departamentos |
| \`employees:manage\` | Empleados canónicos (merge / split / delete) |
| \`share:manage\` | Crear y gestionar links públicos |
| \`report:kpis\` | Ver KPIs del Dashboard |
| \`report:ranking\` | Ver Ranking |
| \`report:ganador\` | Ver Ganador del mes |
| \`report:comparativo\` | Ver Comparativo |
| \`report:tendencia\` | Ver Tendencia |
| \`report:distribucion\` | Ver Distribución |
| \`report:heatmap\` | Ver Heatmap |
| \`report:tabla\` | Ver Tabla |
| \`report:efficiency\` | Ver Eficiencia tx/hr |
| \`report:3d\` | Ver Vista 3D |

### Crear un viewer

1. Click **Agregar usuario**.
2. Email + nombre + contraseña.
3. Rol = \`viewer\`.
4. Marca los permisos que necesite (mínimo uno, si no, no podrá ver nada útil).
5. Asigna departamentos si quieres restringir la data que ve.

### Cambiar contraseña

Desde la tabla → tres puntos → **Cambiar contraseña**. Escribe la nueva, enter. El usuario debe re-loguearse con la nueva contraseña.

### Desactivar

Click 🗑 → confirma. La cuenta queda inactiva pero no se borra. Reactivable desde la misma pantalla quitando el filtro.`,
    tips: [
      {
        kind: "tip",
        text: "Un supervisor de zona puede tener sólo report:ganador + report:ranking, sin upload ni admin. Así ve su podio mensual sin riesgo de romper datos.",
      },
    ],
  },
  {
    id: "links-publicos",
    title: "Links públicos",
    icon: "Share2",
    body: `\`/admin/share\` crea URLs públicas (sin login) para compartir un dashboard de **sólo lectura**.

### 4 alcances (scopes)

| Scope | Para qué |
| --- | --- |
| **Corporativo** | Vista completa de toda la operación. |
| **Departamento** | Filtra a uno o más departamentos. |
| **Supervisor** | Empleados de los departamentos que gestiona un supervisor concreto. |
| **Empleado** | Uno o más empleados específicos. |

### Crear un link

1. Elige el scope (card con el icono).
2. Selecciona departamentos / empleados / supervisor según corresponda.
3. **Periodo**: filtro temporal pre-aplicado (Hoy, Mes, Año…).
4. **Etiqueta** opcional para identificarlo (ej. "Pantalla principal").
5. **Expira en (días)** opcional — si lo dejas vacío, no expira.
6. **PIN** opcional — protege el link con un código numérico.
7. **Vistas a incluir** — chips multi-select de las vistas que el viewer público podrá ver (default: todas).
8. **Vista por defecto** — la primera que se muestra al abrir el link.
9. Click **Generar link**. Aparece un modal con la URL — cópiala o abre directamente.

### Acciones por link generado

- **Copiar** la URL al portapapeles.
- **Abrir** en una nueva pestaña.
- **Modo TV** → abre el link con \`?mode=tv&slides=1\`.
- **Slides** → auto-rotación sin fullscreen.
- **QR** → genera un código QR descargable como PNG (ideal para imprimir y pegar en una pared).
- **Email** → abre el cliente de correo con un mensaje pre-armado.
- **Revocar** → desactiva el link sin borrarlo (badge "Revocado").
- **Eliminar** → borrado permanente.

### Bulk cleanup

Tres botones para limpiar masivamente: **Borrar revocados / Borrar expirados / Borrar TODO el historial**. Útil para hacer limpieza periódica.

### Seguridad

- El link **NO requiere login**. Cualquiera con el link entra.
- Si pones **PIN**, se exige antes de mostrar el dashboard; un JWT de 15 min se guarda en \`sessionStorage\` para no re-preguntar al refrescar.
- Los endpoints públicos están **rate-limited a 60 hits/min/IP**.
- El contador de vistas (\`view_count\`) se incrementa una vez por minuto/IP/token.

### Datos redactados

Antes de enviar al viewer público, el backend elimina IDs internos de Mongo y campos administrativos. El visitante no puede ver \`canonical_id\`, \`department_id\` ni rutas que no estén en \`views_to_include\`.`,
    tips: [
      {
        kind: "warning",
        text: "Cualquiera con el link entra. Trata el link como un \"link mágico\". Usa PIN cuando lo compartas fuera de la organización.",
      },
    ],
  },
  {
    id: "modo-tv",
    title: "Modo TV / Slides",
    icon: "Tv",
    body: `### Configurar una pantalla en bodega

1. Crea un link público con scope **Corporativo** (o el que prefieras) e incluye las vistas que quieres rotar.
2. Click en **Modo TV** → te abre la URL con \`?mode=tv&slides=1&interval=20\`.
3. En el navegador de la TV (Chrome / Edge), idealmente arranca en **modo kiosk**:

\`\`\`
chrome --kiosk --noerrdialogs --disable-infobars URL_DEL_LINK
\`\`\`

4. La app entrará en pantalla completa automáticamente (o si el browser lo bloquea, click en el botón **Pantalla completa**).
5. Cada \`interval\` segundos (default 20) la vista rota a la siguiente (Dashboard → Ganador → Ranking → Tendencia → Distribución → Heatmap → Eficiencia → vuelta).
6. Para salir: mueve el mouse hasta abajo-derecha → click en **Salir** (icono Maximize2).

### Parámetros disponibles

| Parámetro | Valores | Default |
| --- | --- | --- |
| \`mode\` | \`tv\` | (sin TV) |
| \`slides\` | \`1\` | desactivado |
| \`interval\` | nº segundos (mínimo 5) | 20 |
| \`theme\` | \`dark\` / \`light\` | dark |
| \`lang\` | \`es\` / \`en\` | preferencia del navegador |

### QR para empleados

En la card del link, click **QR** → modal con el código. Descarga el PNG y **pégalo en una pared del almacén**. Los empleados escanean con el celular y ven el dashboard sin instalar nada.`,
  },
  {
    id: "ai-mensajes",
    title: "Mensajes AI de seguridad",
    icon: "ShieldAlert",
    body: `### ¿Qué son?

Las cards de **Mensajes AI** que aparecen en el Dashboard y en Ganador son frases cortas generadas por IA (Emergent LLM, modelo \`gpt-4o-mini\`) que rotan automáticamente. Tres categorías:

- **Seguridad** 🛡️ — recordatorios de uso de EPP, cargas seguras, etc.
- **Productividad** ⚡ — tips para mejorar tx/hr.
- **Info** 💡 — datos curiosos / motivacionales.

### Controles

- **Otro mensaje** — fuerza un re-fetch (genera uno nuevo si no hay en caché).
- **Auto-rotar** — toggle on/off; rota cada ~30 s.
- **Generado con IA / Mensaje predefinido** — pequeña pista del origen. Si el LLM falla, se usa una frase pre-seedeada del fallback.

### Idioma

Los mensajes se generan en el idioma activo (\`es\` o \`en\`). Si cambias el toggle, el siguiente mensaje vendrá ya traducido.

> Si no quieres pagar uso de LLM, el sistema funciona igual con frases pre-cargadas — sólo se nota porque la pista pone "Mensaje predefinido".`,
  },
  {
    id: "i18n-tema",
    title: "Idioma y tema",
    icon: "Languages",
    body: `### Idioma

Toggle **ES / EN** en la topbar. Internamente usa \`react-i18next\` y la elección se guarda en \`localStorage.muleria_lang\`.

Cubre **toda la UI**: labels, mensajes de error, días de la semana, meses, tooltips, el manual entero (estas mismas líneas) y los mensajes AI.

### Tema

Toggle **Sol / Luna** en la topbar. Modo **dark** por defecto (paleta Tokyo-Night). La elección se guarda en \`localStorage.muleria_theme\` y se aplica con la clase \`dark\` en el \`<html>\`.

> Los toggles también están en la pantalla de login y en la vista pública compartida.`,
  },
  {
    id: "faq",
    title: "Preguntas frecuentes",
    icon: "HelpCircle",
    body: `### ¿Por qué un empleado aparece dos veces?

Sus aliases no se unificaron. Ve a *Administración → Empleados canónicos*, busca al duplicado y usa **Fusionar**.

### ¿Por qué no veo "Eficiencia tx/hr"?

O bien (a) **falta subir un archivo TE350** (sin horas no hay tx/hr), o bien (b) **tu cuenta no tiene el permiso** \`report:efficiency\`. Pide al admin que te lo añada.

### ¿Por qué el upload dice 0 insertadas y N duplicados?

Re-subiste el mismo archivo. La app deduplica por hash, así que detectó que esas filas ya estaban. No es un error; es la garantía de idempotencia.

### ¿Por qué el link público dice "expirado"?

En el momento de crearlo le pusiste un valor en *Expira en (días)*. Cuando esa fecha pasa, el link queda en estado **expired** y devuelve 410 a quien lo abre. Solución: créalo de nuevo (los links **NO** se renuevan).

### ¿Cómo proyecto el dashboard en una TV?

Genera un link público corporativo, click en **Modo TV**, y abre la URL en Chrome kiosk en la TV. Ver la sección "Modo TV".

### ¿Puedo cambiar la zona horaria o la moneda?

No. La app usa **America/Chicago** fija (cliente en Texas). No maneja moneda — sólo conteos.

### ¿Qué pasa si un Excel viene con columnas distintas?

El parser intenta inferir el tipo por el nombre del archivo. Si las **columnas no coinciden** con el schema, devuelve un 422 con el detalle. Soluciones: (a) re-exporta del ERP, (b) renombra el archivo a uno reconocible, (c) usa el override manual del selector "Tipo de reporte".

### ¿Los empleados con nombre mal escrito se mezclan automáticamente?

No. La unificación se hace **por código** (Z##, 089, TE350) y no por nombre. Si dos transacciones del mismo empleado vienen con códigos distintos sin estar mapeados, aparecerán como dos personas. Usa **Fusionar** manualmente.

### ¿Cómo bloqueo a un empleado del ranking?

Ve a *Empleados canónicos*, selecciónalo, click **Borrar seleccionados**. Queda inactivo y desaparece de todos los reportes. Reactivable cuando quieras.

### ¿Puedo exportar el podio como imagen?

- **Vista 3D**: sí, botón **Descargar PNG** (1600×900).
- **Otros podios**: no hay export integrado. Usa la captura de pantalla del SO o el botón "Imprimir" del navegador (Ctrl/Cmd + P).

### ¿Por qué no aparece la opción "Links públicos" en mi sidebar?

Te falta el permiso \`share:manage\`. Pide al admin.

### ¿Puedo conectar Slack / Teams para notificaciones?

Está en el backlog (P1) pero no implementado. Por ahora la app **no envía notificaciones** — todo es bajo demanda al abrir la URL.`,
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: "Wrench",
    body: `### "Token inválido" / "401 Unauthorized"

Tu JWT expiró (vida útil: 24 h). Vuelve a iniciar sesión.

### "Archivo no soportado"

Sólo \`.xlsx\`. Si exportas como \`.xls\` viejo o CSV, conviértelo primero. Si el archivo correcto da error, comparte la columna problemática con el admin (puede que el ERP haya cambiado el schema).

### "PIN inválido" al abrir un link público

Confirma el PIN con la persona que generó el link. Tras 5 intentos fallidos en un minuto, el rate-limiter te bloquea por 1 minuto (60 hits/min/IP).

### "Demasiadas solicitudes" / 429

El endpoint público rate-limitea a **60 hits/min/IP**. Espera un minuto y vuelve a intentar. Si lo ves recurrentemente, posiblemente tu red está NATeada con muchos clientes — usa otro link o pídele al admin uno con menos rotación.

### Pantalla completa no entra (Modo TV)

Algunos navegadores bloquean \`requestFullscreen()\` sin gesto del usuario. Solución: en la TV, abre el link, mueve el mouse → click en **Pantalla completa** manualmente. **O** lanza Chrome con \`--kiosk\`.

### Los datos no aparecen / vista vacía

Revisa los **filtros globales** (esquina superior del Dashboard). Si pusiste *Hoy* y nadie cargó nada hoy, sale vacío. Click **Restablecer** para volver al preset \`year\`.

### Los gráficos se ven cortados / re-render mal

Refresca la página (\`Ctrl/Cmd + R\`). Es un quirk de Recharts cuando se redimensiona la ventana muy rápido.

### Plotly 3D no carga

La primera carga descarga ~3 MB. Si tu red es lenta, espera unos segundos. Si ves un error en consola tipo \`Cannot find module 'plotly.js-dist-min'\`, avisa al admin — falta una dependencia.`,
  },
  {
    id: "soporte",
    title: "Soporte",
    icon: "LifeBuoy",
    body: `### ¿Necesitas más ayuda?

Si esta documentación no responde tu pregunta:

- **Reportar un bug** o **pedir una feature**: envía un correo a \`<TODO: email-de-soporte@muleria-canales.com>\` con:
  - Captura de pantalla (con la URL visible).
  - Pasos para reproducir.
  - Tu rol y permisos.
- **Capacitación interna**: contacta a \`<TODO: nombre-del-IT-interno>\`.
- **Acceso bloqueado** (perdiste contraseña, cuenta inactiva): habla con el administrador del sistema.

> Esta sección la edita el admin desde el código (\`src/help/manual.es.js\` y \`manual.en.js\`). Reemplaza los placeholders \`<TODO>\` con tus datos reales antes de compartir el manual con tu equipo.`,
  },
];

export default manual;
